// Time Keeping, called Actuals in the PRD and the code (§4.4; renamed on screen 2026-10-15), in three parts picked by the title (rev. 1.12): Hours by week, each person's
// counted hours in US Central for a date range; Inbox, the data-quality items (AC6) summarized by
// person; and Time cards (step B). Codes read "AN001 · Andela" throughout.

import { useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Input, Select, Subtitle2, Tab, TabList } from "@fluentui/react-components";
import { addDays, FLAG_TEXT, HQ_TIME_ZONE, mondayOf, today, type EntryFlag } from "../../../core";
import { api, errorText, send, useApi, type ActualsResponse, type CategoryIndexResponse, type CategoryIndexSaved, type InboxItem, type InboxResponse, type PeopleNamesResponse } from "../api";
import { DateInput } from "../DateInput";
import { useCodeLabels } from "../codes";
import { stamp, weekdayDay } from "../format";
import { go } from "../router";
import { ErrorBar, Loading, Page, useMe, Warnings } from "../ui";
import { CalendarSync } from "./CalendarSync";
import { Periods } from "./Periods";
import { WorkStates } from "./WorkStates";
import { ChangesReport, TimeCards } from "./TimeCards";

const h = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });
const dateLabel = (d: string) => d.slice(0, 10);
const time = (iso: string) => stamp(iso, HQ_TIME_ZONE).slice(11);

const TABS = [
  { key: "hours", label: "Hours by week" },
  { key: "inbox", label: "Inbox" },
  { key: "timecards", label: "Time cards" },
  { key: "changes", label: "Changes" },
  { key: "sync", label: "Calendar sync" },
  { key: "periods", label: "Periods" },
  { key: "states", label: "Work states" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export function Actuals({ tab }: { tab?: string }) {
  const me = useMe();
  // A member sees only their own inbox items and time card (Chris, 2026-10-15); the rest is for admins.
  const admin = me.can("period.close");
  const viewer = admin || me.can("plans.read");
  const tabs = TABS.filter((t) => (t.key === "timecards" ? true : t.key === "inbox" ? admin || me.can("self.read") : t.key === "hours" ? viewer : admin));
  const current: TabKey = (tabs.find((t) => t.key === tab)?.key ?? tabs[0].key) as TabKey;
  const people = useApi<PeopleNamesResponse>(viewer ? "/people?view=names" : null);
  const names = new Map((people.data?.people ?? []).map((p) => [p.id, p.name]));

  return (
    <Page
      title="Time Keeping"
      actions={
        <>
          {viewer && (
            <Button appearance="secondary" onClick={() => go("actuals", "index")}>
              Category index
            </Button>
          )}
          {admin && (
            <Button appearance="secondary" onClick={() => go("actuals", "import")}>
              Import the Timecard Tool…
            </Button>
          )}
        </>
      }
    >
      <TabList selectedValue={current} onTabSelect={(_, d) => go("actuals", String(d.value))} aria-label="Time Keeping sections">
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key}>
            {t.label}
          </Tab>
        ))}
      </TabList>
      <ErrorBar message={people.error} />
      {current === "hours" && <HoursByWeek names={names} />}
      {current === "inbox" && <Inbox names={names} />}
      {current === "timecards" && <TimeCards names={names} />}
      {current === "changes" && <ChangesReport names={names} />}
      {current === "sync" && <CalendarSync names={names} />}
      {current === "periods" && <Periods names={names} />}
      {current === "states" && <WorkStates names={names} />}
    </Page>
  );
}

// --- Hours by week -----------------------------------------------------------------------------------

function HoursByWeek({ names }: { names: Map<string, string> }) {
  const thisMonday = mondayOf(today());
  const [from, setFrom] = useState(addDays(thisMonday, -7 * 7));
  const [to, setTo] = useState(today());
  const [person, setPerson] = useState<string | null>(null);
  const { label } = useCodeLabels();
  const first = mondayOf(from);
  const last = mondayOf(to < from ? from : to);
  const weeks: string[] = [];
  for (let m = first; m <= last && weeks.length < 60; m = addDays(m, 7)) weeks.push(m);
  const actuals = useApi<ActualsResponse>(`/actuals?from=${first}&to=${last}`);

  const rows = new Map<string, Map<string, ActualsResponse["weeks"][number]>>();
  for (const w of actuals.data?.weeks ?? []) {
    if (!rows.has(w.personId)) rows.set(w.personId, new Map());
    rows.get(w.personId)!.set(w.monday, w);
  }
  const byCode = new Map<string, number>();
  for (const w of actuals.data?.weeks ?? []) {
    if (person && w.personId !== person) continue;
    for (const [code, hours] of Object.entries(w.byCode)) byCode.set(code, (byCode.get(code) ?? 0) + hours);
  }
  const total = [...byCode.values()].reduce((a, b) => a + b, 0);

  return (
    <>
      <ErrorBar message={actuals.error} />
      <Card className="panel wide">
        <div className="toolbar">
          <Subtitle2>Hours by week</Subtitle2>
          <label className="inline-field">
            <Caption1>From</Caption1>
            <DateInput size="small" type="date" value={from} onChange={(_, x) => x.value && setFrom(x.value)} aria-label="From" />
          </label>
          <label className="inline-field">
            <Caption1>To</Caption1>
            <DateInput size="small" type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} aria-label="To" />
          </label>
          {actuals.data && <Caption1 className="muted">Counted in US Central. Calendar sync takes over on {dateLabel(actuals.data.cutover)}.</Caption1>}
        </div>
        {actuals.loading ? (
          <Loading />
        ) : rows.size === 0 ? (
          <Body1>No hours in these weeks yet.</Body1>
        ) : (
          <div className="table-scroll">
            <table className="edit-table">
              <thead>
                <tr>
                  <th>Person</th>
                  {weeks.map((w) => (
                    <th key={w} className="num">
                      {dateLabel(w)}
                    </th>
                  ))}
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {[...rows.entries()]
                  .sort(([a], [b]) => (names.get(a) ?? a).localeCompare(names.get(b) ?? b))
                  .map(([personId, byWeek]) => (
                    <tr key={personId} className={person === personId ? "selected clickable" : "clickable"} onClick={() => setPerson(person === personId ? null : personId)} title="Show this person's hours by code">
                      <td>
                        <button type="button" className="link-button" aria-pressed={person === personId}>
                          {names.get(personId) ?? personId}
                        </button>
                      </td>
                      {weeks.map((w) => {
                        const cell = byWeek.get(w);
                        return (
                          <td key={w} className="num" title={cell ? Object.entries(cell.byCode).map(([c, n]) => `${c ? label(c) : "No code"}: ${h(n)} h`).join("\n") : undefined}>
                            {cell ? h(cell.hours) : ""}
                            {cell?.state === "open" && (
                              <Badge appearance="outline" size="small" className="in-effect">
                                Provisional
                              </Badge>
                            )}
                            {cell && cell.review > 0 && (
                              <Badge appearance="tint" color="warning" size="small" className="in-effect">
                                {cell.review}
                              </Badge>
                            )}
                          </td>
                        );
                      })}
                      <td className="num">
                        <strong>{h([...byWeek.values()].reduce((t, c) => t + c.hours, 0))}</strong>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            <Caption1 className="muted">Click a person to see their hours by code below. Hover a cell for that week by code. A number in amber is entries waiting in the inbox.</Caption1>
          </div>
        )}
      </Card>
      {byCode.size > 0 && (
        <Card className="panel wide">
          <div className="toolbar">
            <Subtitle2>Hours by code: {person ? (names.get(person) ?? person) : "everyone"}</Subtitle2>
            {person && (
              <Button size="small" appearance="subtle" onClick={() => setPerson(null)}>
                Show everyone
              </Button>
            )}
          </div>
          <table className="edit-table compact">
            <thead>
              <tr>
                <th>Code</th>
                <th className="num">Hours</th>
                <th className="num">Share</th>
              </tr>
            </thead>
            <tbody>
              {[...byCode.entries()]
                .sort(([, a], [, b]) => b - a)
                .map(([code, hours]) => (
                  <tr key={code}>
                    <td>{code ? label(code) : <span className="muted">No code yet</span>}</td>
                    <td className="num">{h(hours)}</td>
                    <td className="num">{total ? `${Math.round((hours / total) * 100)}%` : ""}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

// --- Inbox -------------------------------------------------------------------------------------------

const itemKey = (i: InboxItem) => `${i.personId}|${i.monday}|${i.entryId}`;

/** "AN001", "an0*", "*andela*": case-insensitive, `*` for any run of characters, matching anywhere. */
export function wildcard(pattern: string): (text: string) => boolean {
  const p = pattern.trim().toLowerCase();
  if (!p) return () => true;
  const re = new RegExp(p.split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*"));
  return (text) => re.test(text.toLowerCase());
}

/**
 * The data-quality inbox (AC6, rev. 1.12). It opens on a count of items by person and the dates they
 * span; open a person, or everyone, to filter and settle items together: assign a code, keep a
 * two-code split, mark not work time, dismiss, or add an unknown category to the index, which settles
 * every entry with that category and future imports.
 */
function Inbox({ names: all }: { names: Map<string, string> }) {
  const me = useMe();
  const inbox = useApi<InboxResponse>("/actuals/inbox");
  // A member's inbox holds only their own items, and settling them is an admin's job.
  const own = !!inbox.data?.own;
  const names = own ? new Map((inbox.data?.items ?? []).map((i) => [i.personId, me.name])) : all;
  const { label, options } = useCodeLabels();
  const [open, setOpen] = useState<string | null>(null); // a person id, "" for everyone, null for the summary only
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [category, setCategory] = useState("");
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();

  const items = inbox.data?.items ?? [];
  const summary = [...items.reduce((m, i) => {
    const s = m.get(i.personId) ?? { count: 0, first: i.day, last: i.day };
    s.count++;
    if (i.day < s.first) s.first = i.day;
    if (i.day > s.last) s.last = i.day;
    return m.set(i.personId, s);
  }, new Map<string, { count: number; first: string; last: string }>())].sort(([a], [b]) => (names.get(a) ?? a).localeCompare(names.get(b) ?? b));

  const matches = wildcard(category);
  const reasons = [...new Set(items.flatMap((i) => i.flags))];
  const shown = open === null
    ? []
    : items.filter(
        (i) =>
          (!open || i.personId === open) &&
          (!from || i.day >= from) &&
          (!to || i.day <= to) &&
          (!category || matches([...i.categories, ...i.codes.map(label)].join(" | ") || "(no category)")) &&
          (!reason || i.flags.includes(reason as EntryFlag)),
      );
  const chosen = shown.filter((i) => selected.has(itemKey(i)));
  const allShownChosen = shown.length > 0 && chosen.length === shown.length;
  const filtered = !!(from || to || category || reason);

  const toggle = (i: InboxItem) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(itemKey(i))) next.delete(itemKey(i));
      else next.add(itemKey(i));
      return next;
    });
  const selectShown = (on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      for (const i of shown) {
        if (on) next.add(itemKey(i));
        else next.delete(itemKey(i));
      }
      return next;
    });

  // Changes made in Outlook after a week froze are settled on their own: take Outlook's version or keep the record (AC1).
  const fromOutlook = chosen.filter((i) => i.outlook);
  const plain = chosen.filter((i) => !i.outlook);
  // Approve accepts items as reported, with every code they have (rev. 1.14); an item with no code needs one first.
  // Hours with no active chargeline can't be approved (2026-10-28): re-code them, or move the project's contract dates.
  const approvable = plain.filter((i) => i.codes.length > 0 && !i.flags.includes("no-chargeline"));
  // Only codes every selected item's day can take.
  const codesByDay = inbox.data?.codesByDay;
  const pickable = codesByDay ? options.filter((c) => plain.every((i) => !i.day || codesByDay[i.day]?.includes(c.code))) : options;
  // Items with no hours to recode: an Out of office flag, an under-reported week, or requested time off
  // missing from the calendar (approve: taken as requested; dismiss: not taken).
  const allOof = plain.length > 0 && plain.every((i) => i.flags.includes("oof-unblocked") || i.flags.includes("under-reported") || i.flags.includes("not-on-calendar"));
  const chosenCategories = [...new Set(plain.flatMap((i) => i.categories))];
  const oneUnknownCategory = plain.length > 0 && chosenCategories.length === 1 && plain.every((i) => i.flags.includes("unknown-code") && i.categories.length === 1);

  const finish = (text: string) => {
    setDone(text);
    setSelected(new Set());
    inbox.reload();
  };

  async function resolve(groups: { codes: string[]; items: InboxItem[] }[], what: string) {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      let resolved = 0;
      for (const g of groups) {
        const r = await send<{ resolved: number; skipped: number }>("POST", "/actuals/resolve-many", {
          items: g.items.map(({ personId, monday, entryId }) => ({ personId, monday, entryId })),
          codes: g.codes,
          note,
        });
        resolved += r.resolved;
      }
      finish(`${resolved} ${resolved === 1 ? "item" : "items"} ${what}.`);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  async function addToIndex() {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      const idx = await api<CategoryIndexResponse>("/category-index");
      const r = await send<CategoryIndexSaved>("PUT", "/category-index", { etag: idx.etag, mappings: [...idx.mappings, { category: chosenCategories[0], code }] });
      finish(`Added "${chosenCategories[0]}" → ${label(code)} to the category index; ${r.recounted} ${r.recounted === 1 ? "entry" : "entries"} recategorized.`);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  async function settleOutlook(action: "accept" | "keep") {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      const r = await send<{ settled: number; skipped: number }>("POST", "/actuals/outlook", {
        items: fromOutlook.map(({ personId, monday, entryId }) => ({ personId, monday, entryId })),
        action,
        note,
      });
      finish(`${r.settled} Outlook ${r.settled === 1 ? "change" : "changes"} ${action === "accept" ? "taken into the record" : "left out; the record stands"}.`);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  async function approve() {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      const r = await send<{ resolved: number; skipped: number }>("POST", "/actuals/resolve-many", {
        items: approvable.map(({ personId, monday, entryId }) => ({ personId, monday, entryId })),
        approve: true,
        note,
      });
      const left = plain.length - approvable.length;
      finish(`${r.resolved} ${r.resolved === 1 ? "item" : "items"} approved as reported.${left ? ` ${left} with no code need recoding first.` : ""}`);
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <>
      <ErrorBar message={inbox.error ?? error} />
      {done && <Warnings intent="info" items={[done]} />}
      <Card className="panel wide">
        <div className="toolbar">
          <Subtitle2>Inbox</Subtitle2>
          {items.length > 0 && (
            <Button size="small" appearance="subtle" onClick={() => setOpen(open === "" ? null : "")}>
              {open === "" ? "Hide items" : `Show all ${items.length}`}
            </Button>
          )}
        </div>
        {own ? (
          <Caption1>
            Your calendar entries the category index can't settle. Fix them in your time card before the week's Tuesday cutoff; after it, an admin
            settles them.
          </Caption1>
        ) : (
          <Caption1>
            Entries the category index can't settle. Open a person to review theirs. An unknown category is best added to the index: that
            recategorizes every entry with it, now and in future imports.
          </Caption1>
        )}
        {inbox.data && inbox.data.heldBack > 0 && (
          <Warnings intent="info" items={[`${inbox.data.heldBack} more in weeks still open; they show after Tuesday's cutoff, since most clear when people categorize on Monday.`]} />
        )}
        {inbox.loading ? (
          <Loading />
        ) : !items.length ? (
          <Body1>Nothing to review.</Body1>
        ) : (
          <table className="edit-table compact inbox-summary">
            <thead>
              <tr>
                <th>Person</th>
                <th className="num">Items</th>
                <th>Weeks</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(([personId, s]) => (
                <tr key={personId} className={open === personId ? "selected clickable" : "clickable"} onClick={() => setOpen(open === personId ? null : personId)}>
                  <td>
                    <button type="button" className="link-button" aria-expanded={open === personId}>
                      {names.get(personId) ?? personId}
                    </button>
                  </td>
                  <td className="num">{s.count}</td>
                  <td>
                    {dateLabel(s.first)}
                    {s.last !== s.first ? ` – ${dateLabel(s.last)}` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open !== null && items.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>{open ? (names.get(open) ?? open) : "Everyone"}</Subtitle2>
          <div className="toolbar">
            <label className="inline-field">
              <Caption1>From</Caption1>
              <DateInput size="small" type="date" value={from} onChange={(_, x) => setFrom(x.value)} aria-label="From date" />
            </label>
            <label className="inline-field">
              <Caption1>To</Caption1>
              <DateInput size="small" type="date" value={to} onChange={(_, x) => setTo(x.value)} aria-label="To date" />
            </label>
            <Input size="small" placeholder="Category: AN001, *andela*" value={category} onChange={(_, x) => setCategory(x.value)} aria-label="Category search" />
            <Select size="small" value={reason} onChange={(_, x) => setReason(x.value)} aria-label="Filter by reason">
              <option value="">Any reason</option>
              {reasons.map((f) => (
                <option key={f} value={f}>
                  {FLAG_TEXT[f]}
                </option>
              ))}
            </Select>
            {filtered && (
              <Button
                size="small"
                appearance="subtle"
                onClick={() => {
                  setFrom("");
                  setTo("");
                  setCategory("");
                  setReason("");
                }}
              >
                Clear filters
              </Button>
            )}
          </div>

          {!own && (
          <div className="toolbar bulk-bar">
            <Caption1>
              <strong>{chosen.length}</strong> of {shown.length} selected
            </Caption1>
            <Select size="small" value={code} onChange={(_, x) => setCode(x.value)} aria-label="Code for the selected items">
              <option value="">Pick a code</option>
              {pickable.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Input size="small" placeholder="Note (optional)" value={note} onChange={(_, x) => setNote(x.value)} aria-label="Note" />
            <Button size="small" appearance="primary" disabled={busy || !code || !plain.length || allOof || !pickable.some((c) => c.code === code)} onClick={() => void resolve([{ codes: [code], items: plain }], `recoded to ${label(code)}`)}>
              Recode
            </Button>
            {oneUnknownCategory && (
              <Button size="small" disabled={busy || !code} onClick={() => void addToIndex()} title="Recategorizes every entry with this category, here or not, and future imports">
                Add “{chosenCategories[0]}” to the index
              </Button>
            )}
            <Button
              size="small"
              disabled={busy || !approvable.length}
              onClick={() => void approve()}
              title="Accept as reported, every code counting: e.g. holiday work overlapping a holiday block, or a two-code meeting split evenly"
            >
              Approve{approvable.length && approvable.length < chosen.length ? ` ${approvable.length}` : ""}
            </Button>
            <Button size="small" disabled={busy || !plain.length} onClick={() => void resolve([{ codes: [], items: plain }], allOof ? "dismissed" : "marked not work time")}>
              {allOof ? "Dismiss" : "Not work time"}
            </Button>
            {fromOutlook.length > 0 && (
              <>
                <Button size="small" disabled={busy} onClick={() => void settleOutlook("accept")} title="The week is past its cutoff; take what Outlook says now into the record">
                  Take Outlook's version{fromOutlook.length < chosen.length ? ` (${fromOutlook.length})` : ""}
                </Button>
                <Button size="small" disabled={busy} onClick={() => void settleOutlook("keep")} title="Leave the record as it was at the cutoff; this change won't be raised again">
                  Keep as recorded{fromOutlook.length < chosen.length ? ` (${fromOutlook.length})` : ""}
                </Button>
              </>
            )}
          </div>
          )}

          <div className="table-scroll">
            <table className="edit-table">
              <thead>
                <tr>
                  {!own && (
                    <th>
                      <input type="checkbox" checked={allShownChosen} onChange={(e) => selectShown(e.target.checked)} aria-label="Select all shown" />
                    </th>
                  )}
                  {!open && <th>Person</th>}
                  <th>When (Central)</th>
                  <th className="num">Hours</th>
                  <th>Category in Outlook</th>
                  <th>Counts toward</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => (
                  <tr key={itemKey(item)} className={selected.has(itemKey(item)) ? "selected" : undefined}>
                    {!own && (
                      <td>
                        <input type="checkbox" checked={selected.has(itemKey(item))} onChange={() => toggle(item)} aria-label={`Select ${names.get(item.personId) ?? item.personId}, ${item.day}`} />
                      </td>
                    )}
                    {!open && <td>{names.get(item.personId) ?? item.personId}</td>}
                    <td>
                      {item.source === "week" ? `Week of ${item.day}` : weekdayDay(item.day)}
                      {item.source === "week" ? "" : item.hours > 0 || item.outlook ? `, ${time(item.start)}–${time(item.end)}` : ", all day"}
                      {item.subject && <Caption1 className="block muted">{item.subject}</Caption1>}
                    </td>
                    <td className="num">{h(item.hours)}</td>
                    <td>{item.categories.join(", ") || <span className="muted">None</span>}</td>
                    <td>{item.codes.map(label).join(", ") || <span className="muted">Nothing yet</span>}</td>
                    <td>
                      {item.flags.map((f) => (
                        <Caption1 key={f} className="block">
                          {f === "outlook-changed" && item.outlook ? item.outlook.text : f === "under-reported" ? `${FLAG_TEXT[f]}: ${h(item.hours)} of ${h(item.expected ?? 0)} h` : FLAG_TEXT[f]}
                        </Caption1>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!shown.length && <Body1>Nothing matches these filters.</Body1>}
          </div>
        </Card>
      )}
    </>
  );
}
